'use memo';

import {
  AgxButton,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_AlertCircleOutline,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import {
  SPECIAL_CHARACTERS,
  FormConfigType,
  StakeholderType,
} from '@urbanx/agx-ui-components';
import { useContext, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { AuthContext } from '@urbanx/agx.auth.client';
import { Tabs } from 'types/Tabs';
import { setAndShowErrorToast } from 'store/config';
import useStateFormLabels from 'features/form/FormLabels/useStateFormLabels';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { formatListOfStrings } from 'utils/formatString';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractDetailModel,
  FormType,
  UploadedDocumentDocumentType,
} from '@urbanx/agx.campaigns.client';

interface ResumeContractRequestProps {
  campaign: CampaignDetailModel;
  contract: ContractDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ResumeContractRequest = ({
  campaign,
  contract,
  currentTab,
  mobile = false,
}: ResumeContractRequestProps) => {
  const { address, campaignId } = campaign;
  const { contractId, documents, buyers, salePrice } = contract;

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);
  const allBuyerNames = getAllBuyerNames(allBuyers);

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const availableForms = useAppSelector(state => state.forms.availableForms);

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;

  const formName = useStateFormLabels(
    campaign.state ?? CampaignDetailModelState.QLD,
    'formName'
  );

  const contractIsGenerated = documents?.some(
    c => c.documentType === UploadedDocumentDocumentType.SalesContract
  );

  if (!availableForms) return;

  const resumeContract = async () => {
    try {
      setDisableButtons(true);

      await loadForm(
        campaignId ?? '',
        contractId ?? '',
        FormConfigType.Campaign
      );
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Contract'));
    }

    setDisableButtons(false);
  };

  const beginContractSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ContractSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (!contractSubmissionForm) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) return;

      const newForm = await completeCampaignStep(
        authToken,
        campaignId ?? '',
        true,
        contract.contractId
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxSpacer
      direction="vertical"
      gap="spacing-xl"
      className="confirmationModalStyle"
    >
      <AgxTypography
        id="contract-request-start-submission-warning"
        type="body"
        size="medium"
      >
        You will not be able to edit the {formName} once you continue.
      </AgxTypography>
      <AgxTypography
        id="contract-request-start-submission-question"
        type="body"
        size="medium"
      >
        Continue to submission?
      </AgxTypography>
    </AgxSpacer>
  );

  const confirmationDialogActions = (
    <AgxSpacer
      direction="horizontal"
      gap="md"
      contentJustification="center"
      fill
      className="confirmationButtonContainerStyle"
    >
      <AgxButton
        id="startsubmission"
        size="large"
        variant="primary"
        text="Yes, start submission"
        onClick={beginContractSubmission}
        disabled={
          disableButtons ||
          (contract?.inProgressForm != null && currentTab === Tabs.Archive)
        }
      />
      <AgxButton
        id="cancel"
        variant="hollow"
        size="large"
        text="No, go back"
        onClick={() => setShowConfirmationModel(false)}
        disabled={disableButtons}
      />
    </AgxSpacer>
  );

  return (
    <>
      <AgxSpacer
        direction="vertical"
        gap="spacing-xl"
        contentAlignment="center"
      >
        <AgxTypography
          id="resume-contract-request-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="resume-contract-request-buyers-sale-price"
          type="body"
          size="medium"
          style={{ textAlign: 'center' }}
        >
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxTypography>
        <AgxSpacer direction="vertical" gap="md" contentAlignment="center">
          {!contractIsGenerated && (
            <AgxSpacer direction="vertical" gap="xs" contentAlignment="center">
              <AgxTypography
                id="resume-contract-request-more-fields"
                type="body"
                size="medium"
                style={{ textAlign: 'center' }}
              >
                There are more fields to fill,
              </AgxTypography>
              <AgxTypography
                id="resume-contract-request-continue"
                type="body"
                size="medium"
                style={{ textAlign: 'center' }}
              >
                let's continue from where you left off
              </AgxTypography>
            </AgxSpacer>
          )}
        </AgxSpacer>
        <Icon_SparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxSpacer>
      <AgxSpacer
        fill
        direction="vertical"
        gap="md"
        contentAlignment="center"
        contentJustification="center"
      >
        <AgxButton
          id="btn-resumeContract"
          variant={contractIsGenerated ? 'primary' : 'hollow'}
          size="large"
          text="Resume Contract"
          onClick={resumeContract}
          disabled={disableButtons}
        />
        {!!contract.envelopeSubmission && contractIsGenerated && (
          <AgxButton
            id="btn-startSubmission"
            variant="hollow"
            size="large"
            disabled={disableButtons || currentTab === Tabs.Archive}
            text="Start Submission"
            onClick={() => setShowConfirmationModel(true)}
          />
        )}
      </AgxSpacer>
      {showConfirmationModel && (
        <AgxModal
          id="resume-contract-start-submission-modal"
          title="Starting a submission?"
          titleIcon={
            <Icon_AlertCircleOutline type="Warning" height={40} width={40} />
          }
          content={confirmationDialogBody}
          actionButtons={confirmationDialogActions}
          actionButtonContainerStyle="withoutDivider"
          type="single"
          variant="withoutDivider"
          setIsDialogOpen={setShowConfirmationModel}
        />
      )}
    </>
  );
};

export default ResumeContractRequest;
